import { useTranslate } from "@tolgee/react";
import { PopoverSelect } from "#/components/ui/popover-select";
import { cn } from "#/lib/utils";

import type { GroupingOption } from "./hooks/use-orderbook.ts";
import type {
	OrderbookSizeUnit,
	OrderbookViewMode,
} from "./types/orderbook.types.ts";

type OrderbookTab = "orderbook" | "trades";

type OrderbookHeaderProps = {
	activeTab: OrderbookTab;
	onTabChange: (tab: OrderbookTab) => void;
};

type OrderbookControlsProps = {
	viewMode: OrderbookViewMode;
	onViewModeChange: (mode: OrderbookViewMode) => void;
	groupingOptions: GroupingOption[];
	selectedGroupingIndex: number;
	onGroupingChange: (index: number) => void;
	coin: string;
	sizeUnit: OrderbookSizeUnit;
	onSizeUnitChange: (unit: OrderbookSizeUnit) => void;
};

function ViewModeIcon({
	mode,
	selected,
	onClick,
}: {
	mode: OrderbookViewMode;
	selected: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				"flex h-5 w-5 cursor-pointer flex-col items-center justify-center gap-[1.5px] rounded border border-transparent transition-[background-color,border-color,opacity] hover:opacity-80",
				selected
					? "border-trade-border-subtle bg-trade-interactive"
					: "bg-transparent",
			)}
			aria-label={`Select ${mode} orderbook view mode`}
			aria-pressed={selected}
		>
			{mode === "both" && (
				<>
					<span className="h-0.5 w-2.5 rounded-full bg-trade-red" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-red" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-green" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-green" />
				</>
			)}
			{mode === "bids" && (
				<>
					<span className="h-0.5 w-2.5 rounded-full bg-trade-green" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-green" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-green" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-green" />
				</>
			)}
			{mode === "asks" && (
				<>
					<span className="h-0.5 w-2.5 rounded-full bg-trade-red" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-red" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-red" />
					<span className="h-0.5 w-2.5 rounded-full bg-trade-red" />
				</>
			)}
		</button>
	);
}

const VIEW_MODES: OrderbookViewMode[] = ["both", "bids", "asks"];

const orderbookSelectTriggerClassName =
	"h-5 min-w-0 gap-1 border-0 bg-transparent px-1 py-0 text-caption1 text-trade-text-secondary shadow-none sm:h-6 sm:border-trade-border-subtle sm:px-2 [&_[data-slot=select-value]]:min-w-0";

const orderbookSelectContentClassName = "rounded-md border-trade-border-subtle";

const orderbookSelectItemClassName = "text-label2";

export function OrderbookHeader({
	activeTab,
	onTabChange,
}: OrderbookHeaderProps) {
	const { t } = useTranslate();
	const tabs: Array<{ value: OrderbookTab; label: string }> = [
		{ value: "orderbook", label: t("orderbook.tab.orderbook") },
		{ value: "trades", label: t("orderbook.tab.trades") },
	];

	return (
		<div className="trade-panel-handle hidden w-full shrink-0 grid-cols-2 border-b border-trade-border-subtle sm:grid">
			{tabs.map((tab) => {
				const isActive = activeTab === tab.value;

				return (
					<button
						key={tab.value}
						type="button"
						onClick={() => onTabChange(tab.value)}
						className={cn(
							"relative flex h-12 min-w-0 items-center justify-center whitespace-nowrap text-label1-bold transition-colors cursor-pointer",
							isActive
								? "text-trade-text-strong"
								: "text-trade-text-faint hover:text-trade-text-secondary",
						)}
					>
						{tab.label}
						{isActive && (
							<span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
						)}
					</button>
				);
			})}
		</div>
	);
}

export function OrderbookControls({
	viewMode,
	onViewModeChange,
	groupingOptions,
	selectedGroupingIndex,
	onGroupingChange,
	coin,
	sizeUnit,
	onSizeUnitChange,
}: OrderbookControlsProps) {
	const sizeUnitOptions: Array<{ label: string; value: OrderbookSizeUnit }> = [
		{ label: "USDC", value: "usdc" },
		{ label: coin, value: "token" },
	];

	const groupingSelectOptions: Array<{ label: string; value: string }> =
		groupingOptions.map((opt, i) => ({
			label: opt.label,
			value: i.toString(),
		}));

	return (
		<div className="trade-panel-handle flex shrink-0 items-center justify-end border-t border-trade-border-subtle py-1 sm:justify-between sm:gap-3 sm:pl-2 sm:py-2">
			<div className="hidden items-center gap-1 sm:flex">
				{VIEW_MODES.map((mode) => (
					<ViewModeIcon
						key={mode}
						mode={mode}
						selected={viewMode === mode}
						onClick={() => onViewModeChange(mode)}
					/>
				))}
			</div>
			<div className="flex min-w-0 items-center gap-2">
				<div className="">
					<PopoverSelect
						ariaLabel="Size unit"
						align="end"
						contentClassName={orderbookSelectContentClassName}
						itemClassName={orderbookSelectItemClassName}
						onValueChange={onSizeUnitChange}
						options={sizeUnitOptions}
						sideOffset={2}
						triggerClassName={orderbookSelectTriggerClassName}
						value={sizeUnit}
					/>
				</div>
				<PopoverSelect
					ariaLabel="Grouping"
					align="end"
					contentClassName={orderbookSelectContentClassName}
					itemClassName={orderbookSelectItemClassName}
					onValueChange={(v) => onGroupingChange(Number(v))}
					options={groupingSelectOptions}
					sideOffset={2}
					triggerClassName={orderbookSelectTriggerClassName}
					value={selectedGroupingIndex.toString()}
				/>
			</div>
		</div>
	);
}
